'use client';

import { Vi } from '@ahamove/icons';
import {
  ErrorCode,
  LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD,
} from '@ahm/api-wrappers-core';
import { local, validPhonePattern } from '@ahm/common-helpers';
import { FloatingLabelInput } from '@ahm/react-ui/components/floating-label-fields';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Divider,
  Group,
  Space,
  Stack,
  rem,
  Modal,
  Text,
  Radio,
} from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../auth-provider';
import {
  AuthStep,
  AuthTab,
  COUNT_DOWN_TIME__REQUEST_SIGNIN,
} from '../lib/constants';
import { usePathname } from '../lib/navigation';
import {
  authLocales,
  createTranslations,
  type Locales,
  systemLocales,
} from '../locales';
import { useAuthStore } from '../store/auth-store-provider';
import FacebookLoginBtn from './facebook-login-btn';
import GoogleLoginBtn from './google-login-btn';
import { SmsIcon, ZaloIcon } from './icons';

type Props = {
  lang?: keyof Locales;
};

export default function SignInForm({ lang = 'vi' }: Props) {
  const tSystem = createTranslations({ locales: systemLocales, lang });
  const tAuth = createTranslations({
    locales: authLocales,
    lang,
  });

  const {
    apiError,
    setApiError,
    phoneNumber,
    facebookAppId,
    googleClientId,
    activeAuthStep,
    authTabScreen,
  } = useAuthStore((state) => state);

  const pathname = usePathname();
  const {
    loginViaPhoneNumber,
    isAuthMutationPending,
    showModalSelectOtpChannel,
    setShowModalSelectOtpChannel,
  } = useAuth();

  const LoginSchema = z.object({
    mobile: z
      .string({ required_error: tAuth('requiredPhoneNumber') })
      .refine((value) => validPhonePattern.test(value), {
        message: tAuth('invalidPhoneNumber'),
      })
      .refine((value) => value.trim() !== '', {
        message: tAuth('requiredPhoneNumber'),
      }),
    otp_method: z.string().optional(),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setFocus,
    resetField,
    watch,
  } = useForm<{ mobile: string; otp_method?: string }>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { mobile: phoneNumber! },
  });

  useEffect(() => {
    if (
      authTabScreen === AuthTab.SignIn ||
      pathname === AuthTab.SignIn.toString() ||
      activeAuthStep === AuthStep.PhoneNumber
    ) {
      setFocus('mobile');
    }
  }, [authTabScreen, setFocus, pathname, activeAuthStep]);

  const loginErrorMessage = useMemo(() => {
    if (
      apiError?.code &&
      ![
        ErrorCode.BannedUser.toString(),
        ErrorCode.DeactivatedUserWrongPasscode.toString(),
        ErrorCode.UserNotFound.toString(),
      ].includes(apiError?.code)
    ) {
      return apiError?.description ?? apiError?.title ?? errors.mobile?.message;
    }
    return errors.mobile?.message;
  }, [apiError, errors]);

  const handleLoginSubmit = (values: {
    mobile: string;
    otp_method?: string;
  }) => {
    setApiError(null);
    let resend = false;
    const prevSentCodePayload = local.getItem(
      LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD
    );

    if (prevSentCodePayload) {
      const prevSent = JSON.parse(prevSentCodePayload);
      if (
        activeAuthStep === AuthStep.OTP &&
        prevSent?._id === values.mobile &&
        prevSent?.time &&
        prevSent.time + COUNT_DOWN_TIME__REQUEST_SIGNIN >
          Math.floor(Date.now() / 1000)
      ) {
        resend = true;
      }
    }

    loginViaPhoneNumber(values, resend);
  };

  // ---------- WATCH FOR MODAL SUBMIT BUTTON ----------
  const otpMethod = watch('otp_method');

  return (
    <>
      <Box
        display="flex"
        w="100%"
        flex={1}
        pb="xs"
        style={{ flexDirection: 'column' }}
      >
        <Stack align="stretch" flex={1}>
          <Box
            display="flex"
            w="100%"
            component="form"
            onSubmit={handleSubmit(handleLoginSubmit)}
            style={{ flexDirection: 'column', gap: rem(16) }}
          >
            <Group wrap="nowrap" gap={8} align="flex-start">
              <Button
                h={rem(48)}
                variant="default"
                type="button"
                leftSection={<Vi className="size-6" />}
              >
                +84
              </Button>

              <Controller
                name="mobile"
                control={control}
                render={({ field: { value, onChange, ...props } }) => (
                  <FloatingLabelInput
                    {...props}
                    autoFocus
                    flex={1}
                    size="sm"
                    autoComplete="tel"
                    label={tAuth('phoneNumber')}
                    value={value}
                    onChange={(value) => onChange(value)}
                    error={loginErrorMessage}
                  />
                )}
              />
            </Group>

            <Button
              type="submit"
              disabled={!isValid}
              fullWidth
              loading={isAuthMutationPending}
              px="md"
              h={rem(48)}
            >
              {tAuth('login')}
            </Button>
          </Box>

          <Space h={rem(32)} w="100%" />

          {googleClientId ?? facebookAppId ? (
            <Divider mb={rem(4)} mt="auto" label={tSystem('or')} />
          ) : null}

          <Stack gap={rem(14)}>
            <FacebookLoginBtn />
            <GoogleLoginBtn />
          </Stack>
        </Stack>
      </Box>

      {/* -------------------- MODAL -------------------- */}
      <Modal
        zIndex={2000}
        maw={400}
        opened={showModalSelectOtpChannel}
        centered
        withCloseButton={false}
        onClose={() => {
          setShowModalSelectOtpChannel(false);
          resetField('otp_method');
        }}
      >
        <Stack
          onSubmit={handleSubmit((values) => {
            handleLoginSubmit(values);
            setShowModalSelectOtpChannel(false);
            resetField('otp_method');
          })}
          component="form"
          gap={8}
        >
          <Text fw={700} fz={22} ta="center" mb={4}>
            Nhận mã xác thực
          </Text>

          <Text ta="center" fz={15} mb={2}>
            {Number(apiError?.extra_params?.fail_passcode_count) > 0
              ? `Bạn đã nhập sai mật khẩu ${apiError?.extra_params?.fail_passcode_count} lần. Bạn sẽ nhận được mã Đăng nhập OTP qua SĐT`
              : 'Bạn sẽ nhận được mã xác thực qua SĐT'}
            <br />
            <Text fw={700} fz={16} span>
              (+84) {phoneNumber?.replace(/^\+?84/, '').replace(/^(0)/, '')}
            </Text>
          </Text>

          <Controller
            name="otp_method"
            control={control}
            render={({ field }) => (
              <Radio.Group value={field.value} onChange={field.onChange}>
                <Stack gap={0}>
                  {/* ZALO OPTION */}
                  <Box
                    onClick={() => field.onChange('zalo')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: `${rem(8)} ${rem(0)}`,
                      minHeight: rem(48),
                    }}
                  >
                    <ZaloIcon />

                    <Text fz={15} fw={500} ml={12} style={{ flex: 1 }}>
                      Zalo
                    </Text>

                    <Radio value="zalo" checked={field.value === 'zalo'} />
                  </Box>

                  {/* SMS OPTION */}
                  <Box
                    onClick={() => field.onChange('sms')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: `${rem(8)} ${rem(0)}`,
                      minHeight: rem(48),
                    }}
                  >
                    <SmsIcon />

                    <Text fz={15} fw={500} ml={12} style={{ flex: 1 }}>
                      Tin nhắn điện thoại
                    </Text>

                    <Radio value="sms" checked={field.value === 'sms'} />
                  </Box>
                </Stack>
              </Radio.Group>
            )}
          />

          <Group mt={16} gap={12} grow>
            <Button
              h={rem(48)}
              variant="default"
              type="button"
              onClick={() => {
                setShowModalSelectOtpChannel(false);
                resetField('otp_method');
              }}
            >
              Hủy
            </Button>

            <Button h={rem(48)} type="submit" fullWidth disabled={!otpMethod}>
              Gửi mã
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
